import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { Play, Film } from 'lucide-react';
// Declare YouTube types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}
export const AboutSection = () => {
  const [activeVideo, setActiveVideo] = useState<number | null>(null);
  const [players, setPlayers] = useState<{
    [key: number]: any;
  }>({});
  const [isAPIReady, setIsAPIReady] = useState(false);
  const playerRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  const trailers = [{
    title: 'Official Experience Trailer',
    description: 'Step into the grandeur of the Sikh Kingdom',
    videoId: '2yMWvJinVgk',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png'
  }, {
    title: 'ਅਧਿਕਾਰਤ ਅਨੁਭਵ ਟ੍ਰੇਲਰ',
    description: 'ਸਿੱਖ ਰਾਜ ਦੀ ਸ਼ਾਨ ਵਿੱਚ ਕਦਮ ਰੱਖੋ',
    videoId: 'erCbP8ed8Hs',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png'
  }];
  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      // If there are existing script tags, insert before the first one
      // Otherwise, append to document head
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = () => {
        setIsAPIReady(true);
      };
    } else {
      setIsAPIReady(true);
    }
  }, []);
  // Create player when video is activated
  useEffect(() => {
    if (!isAPIReady || activeVideo === null || players[activeVideo]) return;
    const playerElement = playerRefs.current[activeVideo];
    if (!playerElement) return;
    const player = new window.YT.Player(playerElement, {
      videoId: trailers[activeVideo].videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 1,
        mute: 1,
        rel: 0,
        modestbranding: 1,
        controls: 1
      },
      events: {
        onReady: (event: any) => {
          event.target.playVideo();
        },
        onStateChange: (event: any) => {
          if (event.data === window.YT.PlayerState.PLAYING) {
            event.target.unMute();
          }
        }
      }
    });
    setPlayers(prev => ({
      ...prev,
      [activeVideo]: player
    }));
  }, [isAPIReady, activeVideo, players, trailers]);
  const handleVideoClick = (index: number) => {
    // Clean up previous player if exists
    if (activeVideo !== null && players[activeVideo]) {
      players[activeVideo].destroy();
      setPlayers(prev => {
        const newPlayers = {
          ...prev
        };
        delete newPlayers[activeVideo];
        return newPlayers;
      });
    }
    setActiveVideo(index);
  };
  return <section id="about" className="relative py-20 md:py-28 bg-black" data-id="element-86" data-mp="36z2o14e3tcbm">
      <div className="container mx-auto px-4" data-id="element-87" data-mp="3bqkn1mwkh2ty">
        <div className="max-w-6xl mx-auto" data-id="element-88" data-mp="3brvsdr5nuin6">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-24" data-id="element-89" data-mp="3gq709wceaj2r">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-90" data-mp="2xywdnrdpcsgo">
              An Immersive Chronicle
            </p>
            <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-91" data-mp="892hjm49o7wp">
              A KINGDOM
              <br data-id="element-92" data-mp="d7nijnrcevm7" />
              <span className="text-amber-400" data-id="element-93" data-mp="d741e99iob98">REBORN</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-94" data-mp="1m6oorl5d3ieh"></div>
          </motion.div>
          {/* Main content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24" data-id="element-95" data-mp="1h8n8t938fz78">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-96" data-mp="1v7d1mcj0rcps">
              <p className="text-white/80 text-lg leading-relaxed mb-6 font-light" data-id="element-97" data-mp="2dxbaguun59uh">
                Step into the court of Maharaja Ranjit Singh, where history
                unfolds not behind glass, but all around you. Through walls that
                transform, floors that transport, and flavors that resurrect a
                golden age.
              </p>
              <p className="text-white/60 leading-relaxed font-light" data-id="element-98" data-mp="2iubjnemsespt">
                This is not theater. This is not dining. This is a portal to
                1825 Lahore—where every sense connects you to the grandeur of
                the Sikh Empire.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-99" data-mp="2xiv0kkt3qfvj">
              <div className="border-l border-white/10 pl-8" data-id="element-100" data-mp="2esdc4sfay6fz">
                <h3 className="font-serif text-2xl text-amber-400 mb-4" data-id="element-101" data-mp="2esviwizlc325">
                  The Experience
                </h3>
                <ul className="space-y-4 text-white/70 font-light" data-id="element-102" data-mp="20uyj7dpubgj9">
                  <li className="flex items-start" data-id="element-103" data-mp="24to5jvsuw8f1">
                    <span className="text-amber-400 mr-3" data-id="element-104" data-mp="bbyvj2na85sb">—</span>
                    <span data-id="element-105" data-mp="bdjquuhf7sr8">
                      A 5-sense immersive experience: sight, sound, scent,
                      taste, touch
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-106" data-mp="1lx5447giba8">
                    <span className="text-amber-400 mr-3" data-id="element-107" data-mp="3ssgo46ng57jm">—</span>
                    <span data-id="element-108" data-mp="3sq40pdyx64z6">
                      360° visual storytelling across walls and floors
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-109" data-mp="pc9n67wf1oej">
                    <span className="text-amber-400 mr-3" data-id="element-110" data-mp="p9f52n0vkfoa">—</span>
                    <span data-id="element-111" data-mp="kakbqr5a78px">
                      Curated drinks and scent design that transport you to the
                      court
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-112" data-mp="353fgglmfu0fe">
                    <span className="text-amber-400 mr-3" data-id="element-113" data-mp="310sgtyjizb7n">—</span>
                    <span data-id="element-114" data-mp="30zhdu0xwm7hl">~60 minutes of complete immersion</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
          {/* Trailers Section */}
          <div id="trailers" className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-115" data-mp="1kkld15ntl9wi">
            {trailers.map((trailer, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.2
          }} className="group" data-id="element-116" data-mp="1ua4x2en9r9sy" data-mpi={index}>
                <div className="relative" data-id="element-117" data-mp="dw4xj4sc5m68">
                  {activeVideo === index ? <div className="aspect-video bg-black rounded-lg overflow-hidden" data-id="element-118" data-mp="iuzst67hp378">
                      <div ref={el => playerRefs.current[index] = el} className="w-full h-full" data-id="element-119" data-mp="iwsyojaa3je6"></div>
                    </div> : <div className="relative aspect-video cursor-pointer overflow-hidden rounded-lg" onClick={() => handleVideoClick(index)} data-id="element-120" data-mp="2uiuu6s8ga17w">
                      <img src={trailer.thumbnail} alt={trailer.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-121" data-mp="21sw0gjjb9fww" />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" data-id="element-122" data-mp="2bph7faew513d"></div>
                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-123" data-mp="3n6h8aufb0o90">
                        <motion.div whileHover={{
                    scale: 1.1
                  }} whileTap={{
                    scale: 0.95
                  }} className="w-20 h-20 rounded-full bg-amber-400/90 backdrop-blur-sm flex items-center justify-center border-2 border-white/20 group-hover:bg-amber-400 transition-colors duration-300" data-id="element-124" data-mp="3i85xohh6q9pq">
                          <Play className="w-10 h-10 text-black ml-1" fill="currentColor" data-id="element-125" data-mp="1no41iht4a6jq" />
                        </motion.div>
                      </div>
                      <div className="absolute top-4 left-4" data-id="element-126" data-mp="1dx9gthvirf0e">
                        <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10" data-id="element-127" data-mp="1ixfbl6x35hmp">
                          <Film className="w-4 h-4 text-amber-400" data-id="element-128" data-mp="26lyhxgwkrkc5" />
                          <span className="text-white text-xs tracking-wider" data-id="element-129" data-mp="2bdgd1obx2qp7">
                            TRAILER
                          </span>
                        </div>
                      </div>
                    </div>}
                  <div className="mt-6" data-id="element-130" data-mp="1i3nuzxyak9rf">
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-131" data-mp="144ofbfsrb57e">
                      {trailer.title}
                    </h3>
                    <p className="text-white/60 font-light" data-id="element-132" data-mp="184yhehdfhbgz">
                      {trailer.description}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
};